import {
    useInfiniteQuery,
    useQuery,
    type QueryClient,
} from "@tanstack/react-query";
import { v7 as uuid } from "uuid";
import { agentChatContract } from "@common/contracts/agent-chat.contract";
import type {
    AgentChatAssistantMessage,
    AgentChatMessage,
    AgentChatUserMessage,
} from "@common/schemas/domains/agentChat/agent-chat.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { retryUnlessStatus } from "./apiError";

const client = getBackendAPIClient(agentChatContract);

const conversationsKey = (workspaceId: string, limit: number) =>
    ["agentChat", "conversations", workspaceId, limit] as const;

const conversationKey = (
    workspaceId: string | null,
    conversationId: string | null,
) => ["agentChat", "conversation", workspaceId, conversationId] as const;

const shouldRetryAgentChatQuery = retryUnlessStatus([404]);

export interface SendMessageInput {
    workspaceId: string;
    conversationId: string | null;
    message: string;
    attachments?: Array<{ financialDocumentId: number }>;
}

interface ConversationCacheEntry {
    conversation: {
        id: string;
        title: string;
        createdAt: string;
        updatedAt: string;
    };
    messages: AgentChatMessage[];
    messageTotalCount: number;
}

// Appends a completed turn (synthetic user row + assistant row) to the
// conversation cache and invalidates the conversation/list queries. Shared by
// the non-streaming `useSendMessage` and the streaming `useAgentChatMessageStream`
// so both keep the cache consistent in exactly the same way.
export function appendTurnToCache(opts: {
    queryClient: QueryClient;
    workspaceId: string;
    conversationId: string;
    userMessage: string;
    assistantMessage: AgentChatAssistantMessage;
}): void {
    const nowIso = new Date().toISOString();
    const syntheticUser: AgentChatUserMessage = {
        id: uuid(),
        role: "user",
        content: opts.userMessage,
        // Optimistic cache row doesn't carry attachment metadata — refetch
        // (triggered by the conversations-list invalidation below) will replace
        // it with the persisted snapshot.
        attachments: null,
        createdAt: nowIso,
    };
    opts.queryClient.setQueryData<ConversationCacheEntry>(
        conversationKey(opts.workspaceId, opts.conversationId),
        (old) => {
            if (old) {
                return {
                    ...old,
                    messages: [
                        ...old.messages,
                        syntheticUser,
                        opts.assistantMessage,
                    ],
                    messageTotalCount: old.messageTotalCount + 2,
                };
            }
            return {
                conversation: {
                    id: opts.conversationId,
                    title: "New chat",
                    createdAt: nowIso,
                    updatedAt: nowIso,
                },
                messages: [syntheticUser, opts.assistantMessage],
                messageTotalCount: 2,
            };
        },
    );
    opts.queryClient.invalidateQueries({
        queryKey: ["agentChat", "conversations", opts.workspaceId],
    });
    opts.queryClient.invalidateQueries({
        queryKey: conversationKey(opts.workspaceId, opts.conversationId),
    });
}

export function useConversations(opts: {
    workspaceId: string;
    limit?: number;
    enabled?: boolean;
}) {
    const limit = opts.limit ?? 10;
    return useInfiniteQuery({
        queryKey: conversationsKey(opts.workspaceId, limit),
        queryFn: async ({ pageParam }) => {
            const res = await client.listConversations({
                query: {
                    workspaceId: opts.workspaceId,
                    limit,
                    offset: pageParam,
                },
                extraHeaders: {
                    [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                },
            });
            return res.body;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce(
                (count, page) => count + page.conversations.length,
                0,
            );
            return loaded < lastPage.totalCount ? loaded : undefined;
        },
        enabled: opts.enabled ?? true,
        retry: shouldRetryAgentChatQuery,
        staleTime: 60_000,
    });
}

export function useConversation(
    conversationId: string | null,
    workspaceId: string,
) {
    return useQuery({
        queryKey: conversationKey(workspaceId, conversationId),
        queryFn: async () => {
            const res = await client.getConversation({
                params: { conversationId: conversationId! },
                query: { workspaceId },
                extraHeaders: {
                    [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                },
            });
            return res.body;
        },
        enabled: Boolean(conversationId),
        retry: shouldRetryAgentChatQuery,
    });
}
